import { createContext, useContext, useReducer, useState } from "react"
import {
    findBlockById,
    addBlock,
    deleteBlock,
    switchBlockPosition,
    switchBlocksPosition,
    selectChild,
    selectParent,
    selectNextSibling,
    selectPrevSibling,
    updateBlock,
    pasteBlock as pasteBlockHelper,
    addBlockTemplate,
    toggleHideBlock
} from "./PageBuilderHelpers"

const PageBuilderContext = createContext(null)

// ---------- STATE INIZIALE ----------
const initialState = {
    page: null,
    publishedPage: null,
    draftPage: null,
    activeVersion: "published",
    hasDraft: false,
    selectedBlockId: null,
    selectedBlockIds: [],
    selectionAnchorId: null,
    history: [],
    historyIndex: -1
}

const flattenBlockIds = (content) => {
    const ids = []

    const walk = (items) => {
        if (!Array.isArray(items)) {
            return
        }
        for (const item of items) {
            if (!item || typeof item !== 'object') {
                continue
            }
            if (item.id) {
                ids.push(item.id)
            }
            if (Array.isArray(item.content) && item.content.length > 0) {
                walk(item.content)
            }
        }
    }

    walk(content)
    return ids
}

const findSiblingsMetaById = (content, targetId, parentId = null) => {
    if (!Array.isArray(content) || !targetId) {
        return null
    }

    const directIndex = content.findIndex((item) => item?.id === targetId)
    if (directIndex !== -1) {
        return {
            parentId,
            siblingIds: content
                .map((item) => item?.id)
                .filter(Boolean)
        }
    }

    for (const item of content) {
        if (!item || typeof item !== 'object') {
            continue
        }
        if (!Array.isArray(item.content) || item.content.length === 0) {
            continue
        }
        const nested = findSiblingsMetaById(item.content, targetId, item.id ?? null)
        if (nested) {
            return nested
        }
    }

    return null
}

const clonePage = page => {
    if (!page) {
        return page
    }
    if (typeof structuredClone === "function") {
        return structuredClone(page)
    }
    return JSON.parse(JSON.stringify(page))
}

const snapshotState = (page, selectedBlockId, selectedBlockIds = [], selectionAnchorId = null) => {
    if (!page) {
        return null
    }

    return {
        page: clonePage(page),
        selectedBlockId: selectedBlockId ?? null,
        selectedBlockIds: Array.isArray(selectedBlockIds) ? [...selectedBlockIds] : [],
        selectionAnchorId: selectionAnchorId ?? null
    }
}

const appendSnapshot = (state, page, selectedBlockId, selectedBlockIds, selectionAnchorId) => {
    if (!page) {
        return {
            history: state.history,
            historyIndex: state.historyIndex
        }
    }

    const baseHistory = state.history.slice(0, state.historyIndex + 1)
    const snapshot = snapshotState(
        page,
        selectedBlockId ?? state.selectedBlockId,
        selectedBlockIds ?? state.selectedBlockIds,
        selectionAnchorId ?? state.selectionAnchorId
    )
    const history = [...baseHistory, snapshot]

    return {
        history,
        historyIndex: history.length - 1
    }
}

// ---------- REDUCER ----------
function pageBuilderReducer(state, action) {
    switch (action.type) {

        case "SWITCH_VERSION": {
            const targetVersion = action.version === "draft" ? "draft" : "published"
            if (targetVersion === "draft" && !state.hasDraft) {
                return state
            }

            const targetPage = targetVersion === "draft"
                ? (state.draftPage ?? [])
                : (state.publishedPage ?? [])

            const initialSnapshot = snapshotState(targetPage, state.selectedBlockId, state.selectedBlockIds, state.selectionAnchorId)

            return {
                ...state,
                activeVersion: targetVersion,
                page: clonePage(targetPage),
                history: initialSnapshot ? [initialSnapshot] : [],
                historyIndex: initialSnapshot ? 0 : -1
            }
        }

        case "SET_PAGE": {
            if (!action.page) {
                return state
            }

            const nextSelectedBlockId = action.selectedBlockId ?? state.selectedBlockId
            const nextSelectedBlockIds = action.selectedBlockIds ?? state.selectedBlockIds
            const nextSelectionAnchorId = action.selectionAnchorId ?? state.selectionAnchorId
            let history = state.history
            let historyIndex = state.historyIndex

            if (action.recordHistory) {
                const appended = appendSnapshot(state, action.page, nextSelectedBlockId, nextSelectedBlockIds, nextSelectionAnchorId)
                history = appended.history
                historyIndex = appended.historyIndex
            }

            const isDraftVersion = state.activeVersion === "draft"

            return {
                ...state,
                page: action.page,
                publishedPage: isDraftVersion ? state.publishedPage : action.page,
                draftPage: isDraftVersion ? action.page : state.draftPage,
                selectedBlockId: nextSelectedBlockId,
                selectedBlockIds: nextSelectedBlockIds,
                selectionAnchorId: nextSelectionAnchorId,
                history,
                historyIndex
            }
        }

        case "SELECT_BLOCK": {
            const nextSelectedBlockId = action.blockId ?? null
            const nextSelectedBlockIds = action.selectedBlockIds ?? (nextSelectedBlockId ? [nextSelectedBlockId] : [])
            const nextSelectionAnchorId = action.selectionAnchorId ?? nextSelectedBlockId ?? null

            const sameIdsLength = Array.isArray(state.selectedBlockIds)
                && Array.isArray(nextSelectedBlockIds)
                && state.selectedBlockIds.length === nextSelectedBlockIds.length
            const sameIds = sameIdsLength
                && state.selectedBlockIds.every((id, index) => id === nextSelectedBlockIds[index])

            if (
                state.selectedBlockId === nextSelectedBlockId
                && state.selectionAnchorId === nextSelectionAnchorId
                && sameIds
            ) {
                return state
            }

            return {
                ...state,
                selectedBlockId: nextSelectedBlockId,
                selectedBlockIds: nextSelectedBlockIds,
                selectionAnchorId: nextSelectionAnchorId
            }
        }

        case "UPDATE_BLOCK": {
            if (!state.page || !action.updates) {
                return state
            }

            const clonedPage = clonePage(state.page)
            const updatedPage = updateBlock(action.blockId, clonedPage, action.updates)

            const appended = appendSnapshot(state, updatedPage, state.selectedBlockId, state.selectedBlockIds, state.selectionAnchorId)

            return {
                ...state,
                page: updatedPage,
                history: appended.history,
                historyIndex: appended.historyIndex
            }
        }

        case "UNDO": {
            if (state.historyIndex <= 0) {
                return state
            }

            const previousSnapshot = state.history[state.historyIndex - 1]

            return {
                ...state,
                page: clonePage(previousSnapshot.page),
                selectedBlockId: previousSnapshot.selectedBlockId ?? null,
                selectedBlockIds: previousSnapshot.selectedBlockIds ?? (previousSnapshot.selectedBlockId ? [previousSnapshot.selectedBlockId] : []),
                selectionAnchorId: previousSnapshot.selectionAnchorId ?? previousSnapshot.selectedBlockId ?? null,
                historyIndex: state.historyIndex - 1
            }
        }

        case "REDO": {
            if (state.historyIndex < 0 || state.historyIndex >= state.history.length - 1) {
                return state
            }

            const nextSnapshot = state.history[state.historyIndex + 1]

            return {
                ...state,
                page: clonePage(nextSnapshot.page),
                selectedBlockId: nextSnapshot.selectedBlockId ?? null,
                selectedBlockIds: nextSnapshot.selectedBlockIds ?? (nextSnapshot.selectedBlockId ? [nextSnapshot.selectedBlockId] : []),
                selectionAnchorId: nextSnapshot.selectionAnchorId ?? nextSnapshot.selectedBlockId ?? null,
                historyIndex: state.historyIndex + 1
            }
        }

        default:
            return state
    }
}

// ---------- PROVIDER ----------
export function PageBuilderProvider({ initialPage, draft, initialVersion = "published", children }) {
    const [selectedHtmlCta, setSelectedHtmlCta] = useState(null)
    const [selectedHtmlText, setSelectedHtmlText] = useState(null)
    const [selectedHtmlImage, setSelectedHtmlImage] = useState(null)
    const hasDraft = draft !== undefined && draft !== null
    const resolvedInitialVersion = initialVersion === "draft" && hasDraft ? "draft" : "published"
    const initialPublishedPage = initialPage || []
    const initialDraftPage = hasDraft ? draft : null
    const initialWorkingPage = resolvedInitialVersion === "draft"
        ? (initialDraftPage || [])
        : initialPublishedPage
    const initialHistory = initialWorkingPage ? [snapshotState(initialWorkingPage, null, [], null)] : []

    const [state, dispatch] = useReducer(pageBuilderReducer, {
        ...initialState,
        page: initialWorkingPage,
        publishedPage: initialPublishedPage,
        draftPage: initialDraftPage,
        activeVersion: resolvedInitialVersion,
        hasDraft,
        history: initialHistory,
        historyIndex: initialHistory.length ? initialHistory.length - 1 : -1
    })

    const mutatePage = updater => {
        if (!state.page) {
            return null
        }
        const clonedPage = clonePage(state.page)
        const result = updater(clonedPage)

        let nextPage = clonedPage
        let meta

        if (
            result &&
            typeof result === "object" &&
            !Array.isArray(result) &&
            Object.prototype.hasOwnProperty.call(result, "page")
        ) {
            nextPage = result.page || clonedPage
            meta = result.meta
        } else if (result) {
            nextPage = result
        }

        dispatch({ type: "SET_PAGE", page: nextPage, recordHistory: true })
        return { page: nextPage, meta }
    }

    const normalizeSelectedIds = (ids, page) => {
        const ordered = flattenBlockIds(page)
        const orderedSet = new Set(ordered)
        const next = []
        const incoming = Array.isArray(ids) ? ids : []

        for (const id of incoming) {
            if (!id || !orderedSet.has(id) || next.includes(id)) {
                continue
            }
            next.push(id)
        }

        return next
    }

    const selectBlockWithOptions = (blockId, options = {}) => {
        const { toggle = false, range = false, additive = false, addOnly = false } = options || {}
        const orderedIds = flattenBlockIds(state.page)
        const orderedSet = new Set(orderedIds)

        if (!blockId || !orderedSet.has(blockId)) {
            dispatch({ type: "SELECT_BLOCK", blockId: null, selectedBlockIds: [], selectionAnchorId: null })
            return
        }

        const currentSelected = normalizeSelectedIds(state.selectedBlockIds, state.page)

        if (range) {
            const anchorId = state.selectionAnchorId && orderedSet.has(state.selectionAnchorId)
                ? state.selectionAnchorId
                : (state.selectedBlockId && orderedSet.has(state.selectedBlockId) ? state.selectedBlockId : blockId)

            const anchorMeta = findSiblingsMetaById(state.page, anchorId)
            const targetMeta = findSiblingsMetaById(state.page, blockId)
            const sameSiblingGroup = !!anchorMeta
                && !!targetMeta
                && anchorMeta.parentId === targetMeta.parentId
                && Array.isArray(anchorMeta.siblingIds)
                && Array.isArray(targetMeta.siblingIds)
                && anchorMeta.siblingIds.length === targetMeta.siblingIds.length
                && anchorMeta.siblingIds.every((id, index) => id === targetMeta.siblingIds[index])

            if (!sameSiblingGroup) {
                dispatch({ type: "SELECT_BLOCK", blockId, selectedBlockIds: [blockId], selectionAnchorId: blockId })
                return
            }

            const siblings = anchorMeta.siblingIds
            const anchorIndex = siblings.indexOf(anchorId)
            const targetIndex = siblings.indexOf(blockId)
            if (anchorIndex === -1 || targetIndex === -1) {
                dispatch({ type: "SELECT_BLOCK", blockId, selectedBlockIds: [blockId], selectionAnchorId: blockId })
                return
            }

            const start = Math.min(anchorIndex, targetIndex)
            const end = Math.max(anchorIndex, targetIndex)
            const rangeIds = siblings.slice(start, end + 1)
            const nextIds = additive
                ? normalizeSelectedIds([...currentSelected, ...rangeIds], state.page)
                : rangeIds

            dispatch({ type: "SELECT_BLOCK", blockId, selectedBlockIds: nextIds, selectionAnchorId: anchorId })
            return
        }

        if (addOnly) {
            const nextIds = currentSelected.includes(blockId)
                ? currentSelected
                : normalizeSelectedIds([...currentSelected, blockId], state.page)

            dispatch({ type: "SELECT_BLOCK", blockId, selectedBlockIds: nextIds, selectionAnchorId: blockId })
            return
        }

        if (toggle) {
            const hasId = currentSelected.includes(blockId)
            const nextIds = hasId
                ? currentSelected.filter((id) => id !== blockId)
                : normalizeSelectedIds([...currentSelected, blockId], state.page)

            const nextActiveId = hasId
                ? (nextIds.length ? nextIds[nextIds.length - 1] : null)
                : blockId

            dispatch({
                type: "SELECT_BLOCK",
                blockId: nextActiveId,
                selectedBlockIds: nextIds,
                selectionAnchorId: hasId ? (state.selectionAnchorId ?? nextActiveId) : blockId
            })
            return
        }

        dispatch({ type: "SELECT_BLOCK", blockId, selectedBlockIds: [blockId], selectionAnchorId: blockId })
    }

    const value = {
        selectedHtmlCta,
        selectedHtmlText,
        selectedHtmlImage,
        // stato
        page: state.page,
        selectedBlockId: state.selectedBlockId,
        selectedBlockIds: state.selectedBlockIds,
        history: state.history,
        historyIndex: state.historyIndex,
        activeVersion: state.activeVersion,
        publishedPage: state.publishedPage,
        draftPage: state.draftPage,
        hasDraft: state.hasDraft,
        canUndo: state.historyIndex > 0,
        canRedo: state.historyIndex >= 0 && state.historyIndex < state.history.length - 1,
        selectedBlock: state.selectedBlockId && state.page
            ? findBlockById(state.selectedBlockId, state.page)
            : null,
        selectedBlocks: Array.isArray(state.selectedBlockIds) && state.page
            ? state.selectedBlockIds
                .map((id) => findBlockById(id, state.page))
                .filter(Boolean)
            : [],

        // helper functions (lettura)
        findBlockById: blockId =>
            state.page ? findBlockById(blockId, state.page) : null,
        selectChild: blockId => {
            if (!state.page) {
                return null
            }
            const block = selectChild(blockId, state.page)
            if (block) {
                dispatch({ type: "SELECT_BLOCK", blockId: block.id, selectedBlockIds: [block.id], selectionAnchorId: block.id })
            }
            return block
        },
        selectParent: blockId => {
            if (!state.page) {
                return null
            }
            const block = selectParent(blockId, state.page)
            if (block) {
                dispatch({ type: "SELECT_BLOCK", blockId: block.id, selectedBlockIds: [block.id], selectionAnchorId: block.id })
            }
            return block
        },
        selectNextSibling: (blockId, options = null) => {
            if (!state.page) {
                return null
            }
            const block = selectNextSibling(blockId, state.page)
            if (block) {
                if (options && typeof options === 'object' && options.addOnly) {
                    selectBlockWithOptions(block.id, { addOnly: true })
                }
                else {
                    dispatch({ type: "SELECT_BLOCK", blockId: block.id, selectedBlockIds: [block.id], selectionAnchorId: block.id })
                }
            }
            return block
        },
        selectPrevSibling: (blockId, options = null) => {
            if (!state.page) {
                return null
            }
            const block = selectPrevSibling(blockId, state.page)
            if (block) {
                if (options && typeof options === 'object' && options.addOnly) {
                    selectBlockWithOptions(block.id, { addOnly: true })
                }
                else {
                    dispatch({ type: "SELECT_BLOCK", blockId: block.id, selectedBlockIds: [block.id], selectionAnchorId: block.id })
                }
            }
            return block
        },

        // azioni che mutano la pagina
        addBlock: (parentId, newBlockType) => {
            const result = mutatePage(page => addBlock(parentId, newBlockType, page))
            const newBlockId = result?.meta?.blockId
            if (newBlockId) {
                dispatch({ type: "SELECT_BLOCK", blockId: newBlockId, selectedBlockIds: [newBlockId], selectionAnchorId: newBlockId })
            }
        },
        addBlockTemplate: (parentId, template) => {
            const result = mutatePage(page => addBlockTemplate(parentId, template, page))
            const newBlockId = result?.meta?.blockId
            if (newBlockId) {
                dispatch({ type: "SELECT_BLOCK", blockId: newBlockId, selectedBlockIds: [newBlockId], selectionAnchorId: newBlockId })
            }
        },
        deleteBlock: blockId => {
            mutatePage(page => deleteBlock(blockId, page))
            const nextIds = state.selectedBlockIds.filter((id) => id !== blockId)
            const nextActiveId = state.selectedBlockId === blockId
                ? (nextIds.length ? nextIds[nextIds.length - 1] : null)
                : state.selectedBlockId
            dispatch({ type: "SELECT_BLOCK", blockId: nextActiveId, selectedBlockIds: nextIds, selectionAnchorId: nextActiveId })
        },
        deleteBlocks: (blockIds = []) => {
            const ids = Array.isArray(blockIds)
                ? blockIds.filter(Boolean)
                : []

            if (ids.length === 0) {
                return
            }

            mutatePage((page) => {
                ids.forEach((id) => {
                    deleteBlock(id, page)
                })
                return page
            })

            dispatch({ type: "SELECT_BLOCK", blockId: null, selectedBlockIds: [], selectionAnchorId: null })
        },
        switchBlockPosition: (blockId, direction) =>
            mutatePage(page => switchBlockPosition(blockId, page, direction)),
        switchBlocksPosition: (blockIds, direction) =>
            mutatePage(page => switchBlocksPosition(blockIds, page, direction)),
        pasteBlock: (targetId, block) => {
            const result = mutatePage(page => pasteBlockHelper(targetId, block, page))
            const newBlockId = result?.meta?.blockId
            if (newBlockId) {
                dispatch({ type: "SELECT_BLOCK", blockId: newBlockId, selectedBlockIds: [newBlockId], selectionAnchorId: newBlockId })
            }
        },
        pasteBlocks: (targetId, blocks = []) => {
            const blocksToPaste = Array.isArray(blocks)
                ? blocks.filter((block) => !!block?.id)
                : []

            if (!targetId || blocksToPaste.length === 0) {
                return []
            }

            const insertedIds = []
            mutatePage((page) => {
                let nextPage = page

                blocksToPaste.forEach((blockToPaste) => {
                    const result = pasteBlockHelper(targetId, blockToPaste, nextPage)
                    if (
                        result &&
                        typeof result === "object" &&
                        !Array.isArray(result) &&
                        Object.prototype.hasOwnProperty.call(result, "page")
                    ) {
                        nextPage = result.page || nextPage
                        if (result?.meta?.blockId) {
                            insertedIds.push(result.meta.blockId)
                        }
                        return
                    }
                    if (result) {
                        nextPage = result
                    }
                })

                return {
                    page: nextPage,
                    meta: {
                        blockIds: insertedIds
                    }
                }
            })

            if (insertedIds.length > 0) {
                const activeId = insertedIds[insertedIds.length - 1]
                dispatch({ type: "SELECT_BLOCK", blockId: activeId, selectedBlockIds: insertedIds, selectionAnchorId: activeId })
            }

            return insertedIds
        },
        updateBlock: (blockId, updates) =>
            dispatch({
                type: "UPDATE_BLOCK",
                blockId,
                updates
            }),
        toggleHideBlock: (blockId, view) => 
            mutatePage(page => { toggleHideBlock(blockId, view, page) }),
        undo: () => dispatch({ type: "UNDO" }),
        redo: () => dispatch({ type: "REDO" }),
        switchVersion: version => dispatch({ type: "SWITCH_VERSION", version }),

        // azioni di stato
        setPage: page =>
            dispatch({ type: "SET_PAGE", page, recordHistory: false }),

        selectBlock: (blockId, options = null) => {
            setSelectedHtmlImage(Number.isInteger(options?.htmlImageIndex) && options.htmlImageIndex >= 0 ? { blockId, index: options.htmlImageIndex } : null)
            setSelectedHtmlText(Array.isArray(options?.htmlTextPath) ? { blockId, path: options.htmlTextPath } : null)
            setSelectedHtmlCta(Number.isInteger(options?.htmlCtaIndex) ? { blockId, index: options.htmlCtaIndex } : null)
            if (!options || typeof options !== 'object') {
                dispatch({ type: "SELECT_BLOCK", blockId, selectedBlockIds: blockId ? [blockId] : [], selectionAnchorId: blockId ?? null })
                return
            }
            selectBlockWithOptions(blockId, options)
        },

        selectBlocks: (blockIds = [], activeBlockId = null) => {
            const nextIds = normalizeSelectedIds(blockIds, state.page)
            const nextActiveId = activeBlockId && nextIds.includes(activeBlockId)
                ? activeBlockId
                : (nextIds.length ? nextIds[nextIds.length - 1] : null)
            dispatch({ type: "SELECT_BLOCK", blockId: nextActiveId, selectedBlockIds: nextIds, selectionAnchorId: nextActiveId })
        },
    }

    return (
        <PageBuilderContext.Provider value={value}>
            {children}
        </PageBuilderContext.Provider>
    )
}

// ---------- HOOK ----------
export function usePageBuilder() {
    const ctx = useContext(PageBuilderContext)
    if (!ctx) {
        throw new Error("usePageBuilder must be used inside PageBuilderProvider")
    }
    return ctx
}

export const useHtmlCtaSelection = () => useContext(PageBuilderContext)?.selectedHtmlCta;

export const useHtmlTextSelection = () => useContext(PageBuilderContext)?.selectedHtmlText;

export const useHtmlImageSelection = () => useContext(PageBuilderContext)?.selectedHtmlImage;
